import { sessionPayload } from '../utils/session';
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchDrivers } from "../redux/slices/driverSlice";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ENDPOINTS from "../utils/constants/endpoints";
import { post } from "../services/api";
import { ROLE_PERMISSIONS } from "../utils/permissions/rolepermissions";
import DriverListMobile from "../mobile/DriverListMobile";
import useIsMobile from "../hooks/useIsMobile";
import { EMPTY_FILTERS, FilterList, STATUS_TABS, filterDrivers } from "../components/FilterList";
import { DASHBOARD_COLORS as C, STATUS_COLORS, focusRing, statusDot } from "../components/DashboardCard";
import { cardStyle, pageStyle } from "../components/busDetails/detailLayout";

// The shared page style tints every button blue; this page colours its own buttons.
const { "& .MuiButton-root": _pageButtons, ...basePage } = pageStyle;
const listPage = { ...basePage, bgcolor: C.page, color: C.navy };
const listCard = { ...cardStyle, height: "auto", borderColor: C.border, overflow: "hidden" };

const TAB_LABELS = { all: "All", pending: "Pending", approved: "Approved", rejected: "Rejected" };
const FILTER_LABELS = { category: "Designation", school: "School", policeStation: "Police Station" };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
    fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: C.primary },
  },
};

const tabSx = (active) => ({
  height: 38,
  px: 1.5,
  gap: 0.75,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  whiteSpace: "nowrap",
  boxShadow: "none",
  border: `1px solid ${active ? C.primary : C.border}`,
  bgcolor: active ? C.primary : "#F8FAFC",
  color: active ? "#fff" : "#334155",
  "&:hover": {
    boxShadow: "none",
    bgcolor: active ? C.primaryHover : C.primarySoft,
    borderColor: active ? C.primaryHover : "#BFDBFE",
  },
  ...focusRing,
});

const countSx = (active) => ({
  minWidth: 22,
  px: 0.75,
  borderRadius: 99,
  fontSize: 12,
  lineHeight: "20px",
  bgcolor: active ? "rgba(255,255,255,.2)" : "#fff",
  color: active ? "#fff" : C.muted,
  border: `1px solid ${active ? "transparent" : C.border}`,
});

// Phones get 40px touch targets; the table keeps compact 32px buttons.
const actionSx = (tone) => ({
  width: { xs: 40, md: 32 },
  height: { xs: 40, md: 32 },
  borderRadius: "8px",
  color: tone,
  border: `1px solid ${C.border}`,
  bgcolor: "#fff",
  "& svg": { fontSize: 18 },
  "&:hover": { bgcolor: alpha(tone, 0.08), borderColor: alpha(tone, 0.35) },
  ...focusRing,
});

function ListEmptyState({ hasQuery, onReset }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", minHeight: 240, p: 3, textAlign: "center" }}>
      <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: C.primarySoft, color: C.primary, display: "grid", placeItems: "center" }}>
        <PersonSearchOutlinedIcon />
      </Box>
      <Typography sx={{ fontWeight: 600, color: C.navy, fontSize: 15 }}>
        {hasQuery ? "No matching driver or conductor records found" : "No driver or conductor records yet"}
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.muted, maxWidth: 360 }}>
        {hasQuery ? "Try a different search term or adjust the filters." : "Records appear here once drivers, conductors or attendants are added."}
      </Typography>
      {hasQuery && (
        <Button onClick={onReset} size="small" sx={{ textTransform: "none", fontWeight: 600, color: C.primary, ...focusRing }}>
          Clear search and filters
        </Button>
      )}
    </Stack>
  );
}

export default function DriverList() {
  const [open, setOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [policeStations, setPoliceStations] = useState([]);
  const [firmTypes, setFirmTypes] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading, error } = useSelector((state) => state.drivers);
  const drivers = useMemo(() => (Array.isArray(list) ? list : []), [list]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [tempFilters, setTempFilters] = useState({ ...EMPTY_FILTERS, status: "all" });
  const { role_id, user } = useSelector((state) => state.auth);
  const permission = ROLE_PERMISSIONS[role_id] || {};
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(fetchDrivers());
  }, [dispatch]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    let playloadPS = {
      token: token,
      users_id: user?.users_id || user?.id,
      role_id: role_id,
      showlist_status: "0",
      limit: "0",
      format: "Json",
    };

    const fetchData = async () => {
      try {
        const [sRes, pRes, , , fRes] = await Promise.all([
          post(ENDPOINTS.MASTER.SCHOOLS, playloadPS),
          post(ENDPOINTS.MASTER.POLICE_STATIONS, playloadPS),
          post(ENDPOINTS.MASTER.RTO_OFFICES, playloadPS),
          post(ENDPOINTS.MASTER.STATES),
          post(ENDPOINTS.MASTER.CATEGORY),
        ]);
        setSchools(sRes?.data || []);
        setPoliceStations(pRes?.data || []);
        setFirmTypes(fRes?.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A new search, tab or filter always starts again from the first page.
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
  }, [search, tab, filters]);

  const filteredDrivers = useMemo(() => filterDrivers(drivers, { search, tab, filters }), [drivers, search, tab, filters]);
  // Tab counts follow the search and panel filters, so each tab shows what clicking it would list.
  const tabCounts = useMemo(
    () => Object.fromEntries(STATUS_TABS.map((status) => [status, filterDrivers(drivers, { search, tab: status, filters }).length])),
    [drivers, search, filters]
  );

  const designationOptions = useMemo(() => {
    const names = firmTypes.length ? firmTypes.map((f) => f.dcc) : drivers.map((d) => d.firm_types);
    return [...new Set(names.filter(Boolean))];
  }, [firmTypes, drivers]);

  const filterValueLabel = (key, value) => {
    if (key === "school") return schools.find((s) => String(s.school_id) === String(value))?.firm_name || value;
    if (key === "policeStation") return policeStations.find((p) => String(p.police_station_id) === String(value))?.firm_name || value;
    return value;
  };
  const activeFilters = Object.keys(EMPTY_FILTERS).filter((key) => filters[key]);
  const hasQuery = Boolean(search.trim()) || activeFilters.length > 0 || tab !== "all";

  const resetAll = () => {
    setSearch("");
    setTab("all");
    setFilters(EMPTY_FILTERS);
  };
  const openFilters = () => {
    setTempFilters({ ...filters, status: tab });
    setOpen(true);
  };
  const applyFilters = () => {
    const { status, ...panelFilters } = tempFilters;
    setFilters(panelFilters);
    setTab(status);
    setOpen(false);
  };
  const resetFilters = () => {
    setTempFilters({ ...EMPTY_FILTERS, status: "all" });
    setFilters(EMPTY_FILTERS);
    setTab("all");
  };

  const handleApprove = async (driver_id) => {
    try {
      const payload = {
        token: localStorage.getItem("token"),
        users_id: sessionPayload().users_id,
        role_id: sessionPayload().role_id,
        driver_id,
      };
      const res = await post(ENDPOINTS.DRIVER.APPROVE, payload);

      if (res?.status === "success") {
        alert("Driver approved successfully!");
        dispatch(fetchDrivers());
      } else {
        alert(res?.["success message"] || "Failed to approve driver");
      }
    } catch (err) {
      console.error("Approve error:", err);
      alert("Something went wrong while approving.");
    }
  };

  const handleDisapprove = async (driver_id) => {
    const reason = prompt("Enter reason for disapproval:");
    if (!reason) return;

    try {
      const payload = {
        token: localStorage.getItem("token"),
        users_id: sessionPayload().users_id,
        role_id: sessionPayload().role_id,
        driver_id,
        reason,
      };
      const res = await post(ENDPOINTS.DRIVER.DISAPPROVE, payload);

      if (res?.status === "success") {
        alert("Driver disapproved successfully!");
        dispatch(fetchDrivers());
      } else {
        alert(res?.["success message"] || "Failed to disapprove driver");
      }
    } catch (err) {
      console.error("Reject error:", err);
      alert("Something went wrong while disapproving.");
    }
  };

  const renderActions = (driver) => {
    const isPending = driver.police_station_level1_status === "Pending";
    const name = driver.contact_person || "record";
    return (
      <>
        <Tooltip title="View">
          <IconButton aria-label={`View ${name}`} onClick={() => navigate(`/drivers/${driver.driver_id}`)} sx={actionSx(C.primary)}>
            <VisibilityOutlinedIcon />
          </IconButton>
        </Tooltip>
        {/* Edit & Delete only for Admin / School */}
        {(role_id === "Mg==" || role_id === "Nw==") && permission.canEdit && isPending && (
          <>
            <Tooltip title="Edit">
              <IconButton aria-label={`Edit ${name}`} onClick={() => navigate(`/drivers/edit/${driver.driver_id}`)} sx={actionSx("#D97706")}>
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton aria-label={`Delete ${name}`} onClick={() => window.confirm("Delete this driver?")} sx={actionSx(STATUS_COLORS.rejected.color)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        {/* Approve / Reject buttons (role based) */}
        {permission.canApprove && isPending && (
          <Tooltip title="Approve">
            <IconButton aria-label={`Approve ${name}`} onClick={() => handleApprove(driver.driver_id)} sx={actionSx(STATUS_COLORS.approved.color)}>
              <CheckCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
        {permission.canReject && isPending && (
          <Tooltip title="Reject">
            <IconButton aria-label={`Reject ${name}`} onClick={() => handleDisapprove(driver.driver_id)} sx={actionSx(STATUS_COLORS.rejected.color)}>
              <HighlightOffIcon />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  const columns = [
    {
      field: "register_no",
      headerName: "Reg No",
      flex: 1,
      renderCell: ({ value }) => <Typography sx={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{value || "—"}</Typography>,
    },
    {
      field: "contact_person",
      headerName: "Name",
      flex: 1.3,
      renderCell: ({ value }) => (
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "firm_types",
      headerName: "Designation",
      flex: 1,
      renderCell: ({ value }) =>
        value ? (
          <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: "6px", bgcolor: C.primarySoft, color: "#1E40AF", fontSize: 12.5, fontWeight: 600 }}>
            {value}
          </Box>
        ) : (
          "—"
        ),
    },
    { field: "mobile1", headerName: "Mobile", flex: 1 },
    { field: "dl_no", headerName: "DL No", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.75}>
          {renderActions(params.row)}
        </Box>
      ),
    },
  ];

  const emptyState = <ListEmptyState hasQuery={hasQuery} onReset={resetAll} />;

  return (
    <Box sx={listPage}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" fontWeight={700} sx={{ fontSize: { xs: 20, sm: 23 }, lineHeight: 1.2, color: C.navy }}>
            Driver / Conductor List
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted }}>Manage drivers, conductors and their verification records</Typography>
        </Box>
        <Tooltip title="Profile">
          <IconButton
            onClick={() => navigate("/profile")}
            aria-label="Open profile"
            sx={{ p: 0.5, border: `1px solid ${C.border}`, bgcolor: "#fff", "&:hover": { bgcolor: C.primarySoft }, ...focusRing }}
          >
            <Avatar src="/profile.png" sx={{ width: 32, height: 32 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Card elevation={0} sx={listCard}>
        {/* Toolbar: status tabs on the left, search and filters on the right; stacks on smaller screens. */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            p: { xs: 1.25, sm: 1.5 },
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <Box
            role="group"
            aria-label="Filter by status"
            sx={{
              display: { xs: "grid", sm: "flex" },
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              flexWrap: "wrap",
              gap: 0.75,
              flex: { xs: "1 1 100%", lg: "0 1 auto" },
            }}
          >
            {STATUS_TABS.map((status) => {
              const isActive = tab === status;
              const statusColor = STATUS_COLORS[status]?.color;
              return (
                <Button key={status} aria-pressed={isActive} onClick={() => setTab(status)} sx={tabSx(isActive)}>
                  {statusColor && <Box component="span" sx={{ ...statusDot(statusColor), boxShadow: isActive ? "0 0 0 2px rgba(255,255,255,.75)" : "none" }} />}
                  {TAB_LABELS[status]}
                  <Box component="span" sx={countSx(isActive)}>
                    {tabCounts[status]}
                  </Box>
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: { xs: "1 1 100%", lg: "0 1 460px" } }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearch("")}
              placeholder="Search by name, mobile, registration no, or email"
              sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], height: 38 } }}
              slotProps={{
                htmlInput: { "aria-label": "Search drivers and conductors" },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: C.muted }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")} edge="end">
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              onClick={openFilters}
              startIcon={<TuneRoundedIcon />}
              aria-label={activeFilters.length ? `Filters, ${activeFilters.length} applied` : "Filters"}
              sx={{
                height: 38,
                flexShrink: 0,
                px: 1.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13.5,
                color: activeFilters.length ? C.primary : "#334155",
                bgcolor: activeFilters.length ? C.primarySoft : "#fff",
                border: `1px solid ${activeFilters.length ? "#BFDBFE" : C.border}`,
                "&:hover": { bgcolor: C.primarySoft, borderColor: "#BFDBFE" },
                ...focusRing,
              }}
            >
              Filters
              {activeFilters.length > 0 && (
                <Box component="span" sx={{ ml: 0.75, minWidth: 20, px: 0.5, borderRadius: 99, bgcolor: C.primary, color: "#fff", fontSize: 12, lineHeight: "20px" }}>
                  {activeFilters.length}
                </Box>
              )}
            </Button>
          </Box>
        </Box>

        {activeFilters.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, px: { xs: 1.25, sm: 1.5 }, py: 1, borderBottom: `1px solid ${C.border}`, bgcolor: "#FBFCFE" }}>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mr: 0.25 }}>Active filters:</Typography>
            {activeFilters.map((key) => (
              <Chip
                key={key}
                size="small"
                label={`${FILTER_LABELS[key]}: ${filterValueLabel(key, filters[key])}`}
                onDelete={() => setFilters({ ...filters, [key]: "" })}
                sx={{ bgcolor: C.primarySoft, color: "#1E40AF", fontWeight: 600, maxWidth: "100%", "& .MuiChip-deleteIcon": { color: "#60A5FA" } }}
              />
            ))}
            <Button size="small" onClick={() => setFilters(EMPTY_FILTERS)} sx={{ textTransform: "none", fontWeight: 600, color: C.muted, minWidth: 0 }}>
              Clear all
            </Button>
          </Box>
        )}

        {error ? (
          <Box sx={{ p: 1.5 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => dispatch(fetchDrivers())}>
                  Retry
                </Button>
              }
            >
              Unable to load driver and conductor records. {typeof error === "string" ? error : ""}
            </Alert>
          </Box>
        ) : isMobile ? (
          <DriverListMobile
            drivers={filteredDrivers}
            loading={loading}
            renderActions={renderActions}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
            emptyState={emptyState}
          />
        ) : (
          <FilterList
            rows={filteredDrivers}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            emptyOverlay={ListEmptyState}
            emptyOverlayProps={{ hasQuery, onReset: resetAll }}
          />
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: "14px" } } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, pb: 1 }}>
          <Box>
            <Typography component="span" sx={{ display: "block", fontWeight: 700, fontSize: 17, color: C.navy }}>
              Filter records
            </Typography>
            <Typography component="span" sx={{ display: "block", fontSize: 13, color: C.muted }}>
              Narrow the list by designation, school, station or status
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small" aria-label="Close filters" sx={{ color: C.muted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: C.border }}>
          <Stack spacing={1.75} sx={{ pt: 0.5 }}>
            <TextField
              select
              size="small"
              label="Designation"
              value={tempFilters.category}
              onChange={(e) => setTempFilters({ ...tempFilters, category: e.target.value })}
              sx={inputSx}
            >
              <MenuItem value="">All designations</MenuItem>
              {designationOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="School"
              value={tempFilters.school}
              onChange={(e) => setTempFilters({ ...tempFilters, school: e.target.value })}
              sx={inputSx}
            >
              <MenuItem value="">All schools</MenuItem>
              {schools.map((s) => (
                <MenuItem key={s.school_id} value={s.school_id}>
                  {s.firm_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Police Station"
              value={tempFilters.policeStation}
              onChange={(e) => setTempFilters({ ...tempFilters, policeStation: e.target.value })}
              sx={inputSx}
            >
              <MenuItem value="">All police stations</MenuItem>
              {policeStations.map((p) => (
                <MenuItem key={p.police_station_id} value={p.police_station_id}>
                  {p.firm_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={tempFilters.status}
              onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
              sx={inputSx}
            >
              {STATUS_TABS.map((status) => (
                <MenuItem key={status} value={status}>
                  {TAB_LABELS[status]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}>
          <Button onClick={resetFilters} sx={{ textTransform: "none", fontWeight: 600, color: C.muted }}>
            Reset filters
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setOpen(false)} sx={{ textTransform: "none", fontWeight: 600, color: "#334155" }}>
              Close
            </Button>
            <Button
              onClick={applyFilters}
              variant="contained"
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", bgcolor: C.primary, "&:hover": { bgcolor: C.primaryHover } }}
            >
              Apply filters
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
